import { Button, Text, Tooltip } from "@fluentui/react-components";
import {
  AppsShieldRegular,
  InfoRegular,
  MailShieldRegular,
  ReceiptMoneyRegular,
  ShieldCheckmarkRegular,
} from "@fluentui/react-icons";
import { EAttachmentType, ETaxonomy, IDocuments } from "library/types";
import { extractFileNameFromUrl, formatDateString, openLinkInNewTab } from "library/utils";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useBenefitsData } from "hooks/useBenefitsData";
import useEmployee from "hooks/useEmployee";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";

import { RevokeConsents } from "./RevokeConsents/RevokeConsents";

type TConsentDocument = IDocuments & {
  consentText?: string;
  dateOfConsent?: string;
  employeeDependentTag?: number;
  consentDocumentUrl?: string;
  implementation?: string;
  package?: string;
  isSensitiveDataConsent?: boolean;
};

export function EmployeeConsents() {
  const { t } = useTranslation();
  const { employeeData } = useEmployee();

  const { enrolledFilteredData, eligibleFilteredData } = useBenefitsData();

  const salaryDeductionAttachmentType = useTaxonomyOptions(ETaxonomy.AttachmentType)?.find(
    (item) => item.code === EAttachmentType.SALARY_DEDUCTION
  )?.value;

  const consentAttachmentType = useTaxonomyOptions(ETaxonomy.AttachmentType)?.find(
    (item) => item.code === EAttachmentType.EMPLOYEE_CONSENT
  )?.value;

  const employeeConsents = employeeData?.consents || [];

  const allBenefitsConsentDocuments = [
    ...(enrolledFilteredData || []),
    ...(eligibleFilteredData || []),
  ]?.reduce(
    (acc, item) => {
      item.enrollmentDocuments?.forEach((doc) => {
        if (doc.attachmentType === salaryDeductionAttachmentType) {
          acc.salaryDeductionConsents.push({
            ...doc,
            isSalaryDeduction: true,
            implementation: item.benefitImplementationName,
            package: item.name,
          });
        }
        if (doc.attachmentType === consentAttachmentType) {
          acc.consents.push({
            ...doc,
            implementation: item.benefitImplementationName,
            package: item.name,
          });
        }
      });
      return acc;
    },
    { salaryDeductionConsents: [] as TConsentDocument[], consents: [] as TConsentDocument[] }
  );

  const consentDocumentsAsList = (
    [
      ...(employeeConsents || []),
      ...(allBenefitsConsentDocuments?.salaryDeductionConsents || []),
      ...(allBenefitsConsentDocuments?.consents || []),
    ] as TConsentDocument[]
  )
    .map((item) => {
      return {
        ...item,
        dateOfConsent: item.dateOfConsent || item?.lastModifiedAt || "",
      };
    })
    .sort((a, b) => new Date(b.dateOfConsent).valueOf() - new Date(a.dateOfConsent).valueOf());

  if (!consentDocumentsAsList?.length) {
    return (
      <NoDocumentsWrap>
        <InfoRegular fontSize={18} />
        <Text>{t("hbh.noConsentsGiven.label")}</Text>
      </NoDocumentsWrap>
    );
  }

  return (
    <>
      <RevokeConsents />
      <Row>
        <Text weight="semibold">{t("hbh.name.label")}</Text>
        <Text weight="semibold">{t("hbh.info.label")}</Text>
        <Text weight="semibold" align="end">
          {t("hbh.date.label")}
        </Text>
      </Row>

      {consentDocumentsAsList?.map((doc: TConsentDocument) => {
        const isSensitiveDataConsent = doc?.isSensitiveDataConsent;

        const hasConsentUrl = !!doc.consentDocumentUrl;

        if (hasConsentUrl) {
          return (
            <Row key={doc.dateOfConsent}>
              <Tooltip
                relationship="description"
                content={
                  isSensitiveDataConsent
                    ? t("hbh.sensitiveDataConsent.label")
                    : t("hbh.privacyNotice.label")
                }
              >
                <DocItemButton
                  data-help="profile-consent-document"
                  onClick={() => {
                    if (!hasConsentUrl) return;
                    openLinkInNewTab(doc.consentDocumentUrl || "");
                  }}
                  icon={isSensitiveDataConsent ? <ShieldCheckmarkRegular /> : <MailShieldRegular />}
                  appearance="subtle"
                  style={{
                    paddingLeft: 0,
                    justifyContent: "flex-start",
                  }}
                >
                  {hasConsentUrl && (
                    <TruncatedText truncate block wrap={false}>
                      {extractFileNameFromUrl(doc.consentDocumentUrl)}
                    </TruncatedText>
                  )}
                </DocItemButton>
              </Tooltip>
              <div />
              <Text align="end">{formatDateString(doc?.dateOfConsent || "")}</Text>
            </Row>
          );
        }

        const docPath = doc.attachmentPath;
        const docName = extractFileNameFromUrl(docPath);

        const implementationName = doc.implementation ? `${doc?.implementation}` : "";
        const packageName = doc?.package ? ` / ${doc.package}` : "";
        return (
          <Row key={docPath}>
            <Tooltip
              relationship="description"
              content={
                doc.isSalaryDeduction
                  ? t("hbh.salaryDeductionConsent.label")
                  : t("hbh.insurerConsent.label")
              }
            >
              <DocItemButton
                data-help="profile-consent-document"
                onClick={() => {
                  openLinkInNewTab(docPath || "");
                }}
                icon={doc?.isSalaryDeduction ? <ReceiptMoneyRegular /> : <AppsShieldRegular />}
                appearance="subtle"
                style={{
                  paddingLeft: 0,
                  justifyContent: "flex-start",
                }}
              >
                <TruncatedText truncate block wrap={false}>
                  {docName}
                </TruncatedText>
              </DocItemButton>
            </Tooltip>

            <Text>
              {implementationName}
              {packageName}
            </Text>

            <Text align="end">{formatDateString(doc?.dateOfConsent || "")}</Text>
          </Row>
        );
      })}
    </>
  );
}
const Row = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr 1fr;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLight};
  padding: 10px;
  align-items: center;
`;

const DocItemButton = styled(Button)`
  display: flex;
  text-align: left;
  text-decoration: underline;
  width: 100%;
`;

const NoDocumentsWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 40px;
  padding-left: 10px;
`;

const TruncatedText = styled(Text)`
  max-width: 100%;
  overflow: hidden;
`;
